import { useEffect, useState } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import arrowRightIcon from 'app/images/icons/arrowRight.svg';
import costIcon from 'app/images/icons/costBlack.svg';
import timeIcon from 'app/images/icons/time.svg';
import { Notification } from 'app/views/Notifications/Notification';
import { DurationCounter } from 'components/atoms/DurationCounter';
import getMonetaryRevenue from 'handlers/get-monetary-revenue';
import moment from 'moment';
import apiSlice from 'services/api';
import { clearParkingAndReservation } from 'services/parkingAndReservations/actions';
import { selectActiveParking } from 'services/parkingAndReservations/selectors';
import { selectIsChargerOpen } from 'services/portal/selectors';
import { selectPublicSites, selectSiteVatMultiplier } from 'services/sites/selectors';
import { selectTransaction } from 'services/transactions/selectors';

export const ParkingSnackbar = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const activeParking = useSelector(selectActiveParking);
  const activeParkingTransactionId = activeParking?.transactionId;
  // TODO: Code should handle better if there is no active parking
  const transactionFromStore = useSelector(selectTransaction(activeParkingTransactionId ?? ''));
  const transactionList = apiSlice.useGetTransactionQuery(activeParkingTransactionId ?? '', {
    skip: !!transactionFromStore,
  });
  const lastTransactionData = transactionFromStore ?? transactionList?.data?.transaction;
  const activeParkingDeviceUuid = activeParking?.deviceUuid;
  const freeParkingSeconds = (activeParking?.freeMinutes ?? 0) * 60;
  const vatMultiplier = useSelector(selectSiteVatMultiplier(activeParking?.siteUuid ?? '')) ?? 1;
  const parkingEurosPerMinute = ((activeParking?.priceCentsPerMinute ?? 0) / 100) * vatMultiplier;
  const startParkingTimestamp = activeParking?.startTime;

  const remainingSeconds = moment().diff(moment(startParkingTimestamp), 'seconds');
  const paidParkingStart = moment(startParkingTimestamp).add(freeParkingSeconds, 'seconds');
  const isAfterPaidTime = moment().isAfter(paidParkingStart);
  const lastTransactionConsuption = (lastTransactionData?.amount ?? 0) / 1000;
  const transactionCostEuros = getMonetaryRevenue(lastTransactionData?.monetaryRevenueWithVAT ?? 0);
  const [isPaidParking, setIsPaidParking] = useState(isAfterPaidTime);
  const [paidParkingCostEuros, setPaidParkingCostEuros] = useState(0);
  const publicSites = useSelector(selectPublicSites);
  const isChargerOpen = useSelector(selectIsChargerOpen);
  const { refetch } = apiSlice.useGetActiveParkingAndReservationQuery(undefined);

  const handleNavigateToParkedDevice = () => {
    navigate(`/sites?site=${activeParking?.siteUuid}&device=${activeParkingDeviceUuid}`);
  };

  const calculatePaidParkingPrice = () => {
    const paidParkingMinutes = moment().diff(moment(paidParkingStart), 'seconds') / 60;
    return paidParkingMinutes * parkingEurosPerMinute;
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (isPaidParking) {
      intervalId = setInterval(() => {
        setPaidParkingCostEuros(calculatePaidParkingPrice());
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isPaidParking]);

  useEffect(() => {
    refetch();
  }, [publicSites]);

  return (
    <div className="relative flex w-full">
      <Notification
        onClick={handleNavigateToParkedDevice}
        backgroundColor="bg-raisingBlack"
        className="h-[72px]"
      >
        <div className="flex justify-between items-center w-full">
          <div className="flex justify-start items-center">
            <CountdownCircleTimer
              isPlaying={true}
              size={55}
              strokeWidth={3}
              duration={freeParkingSeconds}
              trailColor="#eb4e20"
              colors={freeParkingSeconds === 0 ? '#eb4e20' : '#5e5e5e'}
              initialRemainingTime={freeParkingSeconds - remainingSeconds}
              onComplete={() => ({
                shouldRepeat: false,
              })}
            >
              {() => (
                <img src={isPaidParking ? costIcon : timeIcon} alt="time" className="invert" />
              )}
            </CountdownCircleTimer>
            <div className="items-start ml-3">
              <p className="font-normal text-white">
                <span className="flex text-sm">
                  <DurationCounter
                    datetime={
                      remainingSeconds < freeParkingSeconds
                        ? startParkingTimestamp
                        : paidParkingStart
                    }
                    maxTimeSeconds={freeParkingSeconds}
                    onMaxTimeAction={() =>
                      parkingEurosPerMinute === 0
                        ? dispatch(clearParkingAndReservation())
                        : setIsPaidParking(true)
                    }
                  />
                  <span className="pl-2">
                    {isPaidParking && `-  ${paidParkingCostEuros.toFixed(2)} €`}
                  </span>
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-xs opacity-50">
                    {`${t('chargingFinished', 'Charging finished')}:
                    ${
                      lastTransactionConsuption > 0 ? lastTransactionConsuption.toFixed(2) : '0.00'
                    } kWh -
                    ${transactionCostEuros > 0 ? transactionCostEuros.toFixed(2) : '0.00'} €`}
                  </span>
                  <span className="text-xs opacity-50">
                    {isPaidParking
                      ? t('paidParking', 'Paid parking')
                      : t('freeParking', 'Free parking')}
                  </span>
                </span>
              </p>
            </div>
          </div>
          {!isChargerOpen && !activeParking && (
            <img src={arrowRightIcon} alt="arrow" className="filter invert w-6 h-6 mr-3" />
          )}
        </div>
      </Notification>
    </div>
  );
};
